import React, { useState } from "react";
import {
  Box, Chip, IconButton, MenuItem, Paper, TextField, Typography, alpha,
  Accordion, AccordionSummary, AccordionDetails,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArticleIcon from "@mui/icons-material/Article";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useGetSystemLogsQuery } from "../../../Api/attoDeskApi";
import { useAppSelector } from "../../../Store/hooks";
import { selectIsSuperAdmin } from "../../../Store/Auth/AuthSelector";
import PageLoader from "../../../Components/Loader/PageLoader";
import {
  NAVY, BORDER, TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../../theme/tokens";

interface ILogEntry {
  timestamp: string;
  env: string;
  level: string;
  message: string;
  extra: string;
}

const LEVEL_COLOR: Record<string, { bg: string; fg: string }> = {
  emergency: { bg: "#fee2e2", fg: "#991b1b" },
  alert:     { bg: "#fee2e2", fg: "#991b1b" },
  critical:  { bg: "#fee2e2", fg: "#991b1b" },
  error:     { bg: "#fee2e2", fg: "#991b1b" },
  warning:   { bg: "#fef3c7", fg: "#92400e" },
  notice:    { bg: "#dbeafe", fg: "#1e40af" },
  info:      { bg: "#dbeafe", fg: "#1e40af" },
  debug:     { bg: "#f1f5f9", fg: "#475569" },
};

const LogsPage: React.FC = () => {
  const isSuper = useAppSelector(selectIsSuperAdmin);
  const [level, setLevel] = useState<string>("");
  const [limit, setLimit] = useState<number>(200);

  const { data, isLoading, refetch, isFetching } = useGetSystemLogsQuery(
    { level: level || undefined, limit },
    { skip: !isSuper },
  );

  if (!isSuper) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <Typography sx={{ fontSize: 14, color: TEXT_SECONDARY }}>
          Server logs are visible to Super Admins only.
        </Typography>
      </Box>
    );
  }
  if (isLoading) return <PageLoader height="20vh" />;

  const entries: ILogEntry[] = Array.isArray(data?.data) ? (data?.data as unknown as ILogEntry[]) : [];
  const meta = (data as any)?.meta || {};

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 1.5,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: `linear-gradient(135deg, ${alpha("#475569", 0.18)} 0%, ${alpha("#475569", 0.06)} 100%)`,
          color: "#475569",
        }}>
          <ArticleIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography fontSize={20} fontWeight={800} color={NAVY} letterSpacing="-0.02em">
            Laravel Logs
          </Typography>
          <Typography fontSize={12} color={TEXT_SECONDARY}>
            {meta.exists === false ? "Log file does not exist yet." :
              `${entries.length} entries${meta.size_bytes ? ` · ${(meta.size_bytes / 1024).toFixed(0)} KB total` : ""}`}
            {isFetching ? " · refreshing…" : ""}
          </Typography>
        </Box>
        <IconButton onClick={() => refetch()}
          sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, width: 38, height: 38, bgcolor: "#fff" }}>
          <RefreshIcon sx={{ fontSize: 18, color: NAVY }} />
        </IconButton>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 1.5, p: 2, alignItems: "center" }}>
          <TextField select size="small" label="Level" value={level}
            onChange={(e) => setLevel(e.target.value)}
            sx={{ width: 200, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, bgcolor: "#fff" } }}>
            <MenuItem value="">All levels</MenuItem>
            <MenuItem value="error">Error</MenuItem>
            <MenuItem value="warning">Warning</MenuItem>
            <MenuItem value="info">Info</MenuItem>
            <MenuItem value="debug">Debug</MenuItem>
          </TextField>
          <TextField select size="small" label="Limit" value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            sx={{ width: 140, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, bgcolor: "#fff" } }}>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
            <MenuItem value={200}>200</MenuItem>
            <MenuItem value={500}>500</MenuItem>
            <MenuItem value={1000}>1000</MenuItem>
          </TextField>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: "#fff", overflow: "hidden" }}>
        {entries.length === 0 ? (
          <Box sx={{ p: 6, textAlign: "center", color: TEXT_SECONDARY, fontSize: 13 }}>
            No log entries match this filter.
          </Box>
        ) : entries.map((e, i) => {
          const c = LEVEL_COLOR[e.level.toLowerCase()] || LEVEL_COLOR.debug;
          return (
            <Accordion key={i} disableGutters elevation={0}
              sx={{ borderBottom: `1px solid ${BORDER}`, "&:before": { display: "none" } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}
                sx={{ "& .MuiAccordionSummary-content": { my: 1, gap: 2, alignItems: "center" } }}>
                <Chip size="small" label={e.level.toUpperCase()}
                  sx={{ height: 20, fontSize: 10, fontWeight: 700,
                    bgcolor: c.bg, color: c.fg, minWidth: 80 }} />
                <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, fontFamily: "monospace",
                  whiteSpace: "nowrap" }}>
                  {e.timestamp}
                </Typography>
                <Typography sx={{ fontSize: 13, color: TEXT_PRIMARY,
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  flex: 1 }}>
                  {e.message}
                </Typography>
              </AccordionSummary>
              {e.extra && e.extra.trim().length > 0 && (
                <AccordionDetails sx={{ bgcolor: "#0b1e2d", color: "#cbd5e1" }}>
                  <Box component="pre" sx={{
                    m: 0, fontSize: 11, fontFamily: "monospace",
                    whiteSpace: "pre-wrap", wordBreak: "break-all",
                  }}>
                    {e.extra}
                  </Box>
                </AccordionDetails>
              )}
            </Accordion>
          );
        })}
      </Paper>
    </Box>
  );
};

export default LogsPage;
